import React, { useState, useRef, useEffect } from 'react';
import { 
  ThumbsUp, ThumbsDown, MessageCircle, Share2, 
  Play, Pause, Volume2, VolumeX, MoreVertical,
  ExternalLink, Copy, Check, Heart, Eye
} from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { supabase } from '../../lib/supabase';

interface VideoCardProps {
  video: {
    id: string;
    title: string;
    video_url: string;
    thumbnail_url: string | null;
    tags: string[];
    product_links: any[];
    view_count: number;
    created_at: string;
    seller: {
      id: string;
      organization: string;
      location: string;
      verified: boolean;
    };
    reactions: {
      interested_count: number;
      not_interested_count: number;
      user_reaction: string | null;
    };
    comments_count: number;
  };
  isVisible: boolean;
  isMobile: boolean;
  onReaction: (videoId: string, reactionType: 'interested' | 'not_interested') => void;
  onComment: (videoId: string) => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ 
  video, 
  isVisible, 
  isMobile,
  onReaction, 
  onComment 
}) => {
  const { user } = useSupabase();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [hasViewed, setHasViewed] = useState(false);
  const [videoLoadError, setVideoLoadError] = useState(false);

  // Check if video URL is valid on component mount
  useEffect(() => {
    if (!video.video_url || typeof video.video_url !== 'string' || video.video_url.trim() === '') {
      setVideoLoadError(true);
    }
  }, [video.video_url]);

  // Auto-play when visible
  useEffect(() => {
    if (videoRef.current) {
      if (isVisible) {
        // Check if video has valid sources before attempting to play
        if (videoRef.current.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
          setVideoLoadError(true);
          return;
        }
        
        videoRef.current.play().then(() => {
          setIsPlaying(true);
          // Increment view count only once per session
          if (!hasViewed) {
            incrementViewCount();
            setHasViewed(true);
          }
        }).catch((error) => {
          // Ignore AbortError which occurs when play() is interrupted by pause()
          if (error.name !== 'AbortError') {
            console.error('Video playback error:', error);
            setVideoLoadError(true);
          }
        });
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isVisible]);

  const incrementViewCount = async () => {
    try {
      await supabase.rpc('increment_video_view_count', { video_uuid: video.id });
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleReaction = (reactionType: 'interested' | 'not_interested') => {
    if (!user) {
      alert('Please sign in to react to videos');
      return;
    }
    onReaction(video.id, reactionType);
  };

  const handleShare = (platform?: string) => {
    const videoUrl = `${window.location.origin}/video/${video.id}`;
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(videoUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      setShowShareMenu(false);
      return;
    }
    
    let shareUrl = '';
    const title = `Check out this video: ${video.title}`;
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(videoUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(videoUrl)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(videoUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(title + ' ' + videoUrl)}`;
        break;
      default:
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank');
    }
    
    setShowShareMenu(false);
  };

  const formatCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  return (
    <div className="relative w-full h-full bg-black rounded-lg overflow-hidden group touch-manipulation">
      {/* Video */}
      {videoLoadError ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-800 min-h-[400px] md:min-h-[500px]">
          <div className="text-center text-white">
            <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Play className="w-8 h-8" />
            </div>
            <p className="text-lg font-medium mb-2">Video not available</p>
            <p className="text-sm text-gray-300">Unable to load video content</p>
          </div>
        </div>
      ) : (
        <video
          ref={videoRef}
          src={video.video_url}
          poster={video.thumbnail_url || undefined}
          className="w-full h-full object-cover min-h-[400px] md:min-h-[500px]"
          loop
          muted={isMuted}
          playsInline
          crossOrigin="anonymous"
          onClick={togglePlayPause}
          onError={() => setVideoLoadError(true)}
        />
      )}
      
      {/* Overlay Controls */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30">
        {/* Top Info */}
        <div className="absolute top-2 md:top-4 left-2 md:left-4 right-2 md:right-4 flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm md:text-base">
              {video.seller.organization.charAt(0)}
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-white font-medium text-sm md:text-base">{video.seller.organization}</span>
                {video.seller.verified && (
                  <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                    <Check className="w-2 h-2 text-white" />
                  </div>
                )}
              </div>
              <span className="text-white/80 text-xs md:text-sm">{video.seller.location}</span>
            </div>
          </div>
          
          <button className="text-white/80 hover:text-white">
            <MoreVertical className="w-5 h-5" />
          </button>
        </div>
        
        {/* Center Play/Pause */}
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              onClick={togglePlayPause}
              className="w-12 h-12 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <Play className="w-6 h-6 md:w-8 md:h-8 ml-1" />
            </button>
          </div>
        )}
        
        {/* Bottom Info */}
        <div className="absolute bottom-0 left-0 right-0 p-2 md:p-4">
          <div className="flex items-end justify-between">
            {/* Video Info */}
            <div className="flex-1 mr-2 md:mr-4">
              <h3 className="text-white font-semibold text-sm md:text-lg mb-1 md:mb-2 line-clamp-2">{video.title}</h3>
              
              {/* Tags */}
              {video.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 md:gap-2 mb-2 md:mb-3">
                  {video.tags.slice(0, isMobile ? 2 : 3).map((tag, index) => (
                    <span key={index} className="bg-white/20 backdrop-blur-sm text-white px-1.5 md:px-2 py-0.5 md:py-1 rounded-full text-xs">
                      #{tag}
                    </span>
                  ))}
                  {video.tags.length > (isMobile ? 2 : 3) && (
                    <span className="text-white/80 text-xs">+{video.tags.length - (isMobile ? 2 : 3)} more</span>
                  )}
                </div>
              )}
              
              {/* Product Links */}
              {video.product_links.length > 0 && !isMobile && (
                <div className="flex items-center space-x-2 mb-2">
                  <ExternalLink className="w-4 h-4 text-white/80" />
                  <span className="text-white/80 text-sm">
                    {video.product_links.length} product{video.product_links.length > 1 ? 's' : ''} featured
                  </span>
                </div>
              )}
              
              {/* View Count */}
              <div className="flex items-center space-x-1 text-white/80 text-xs md:text-sm">
                <Eye className="w-3 h-3 md:w-4 md:h-4" />
                <span>{formatCount(video.view_count)} views</span>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className={`flex ${isMobile ? 'flex-row space-x-2' : 'flex-col space-y-4'}`}>
              {/* Reactions */}
              <div className={`flex ${isMobile ? 'flex-col items-center space-y-1' : 'flex-col items-center space-y-2'}`}>
                <button
                  onClick={() => handleReaction('interested')}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                    video.reactions.user_reaction === 'interested'
                      ? 'bg-green-500 text-white'
                      : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
                  }`}
                >
                  <ThumbsUp className={`${isMobile ? 'w-4 h-4' : 'w-6 h-6'}`} />
                </button>
                <span className="text-white text-xs font-medium text-center">
                  {formatCount(video.reactions.interested_count)}
                </span>
              </div>
              
              <div className={`flex ${isMobile ? 'flex-col items-center space-y-1' : 'flex-col items-center space-y-2'}`}>
                <button
                  onClick={() => handleReaction('not_interested')}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                    video.reactions.user_reaction === 'not_interested'
                      ? 'bg-red-500 text-white'
                      : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
                  }`}
                >
                  <ThumbsDown className={`${isMobile ? 'w-4 h-4' : 'w-6 h-6'}`} />
                </button>
                <span className="text-white text-xs font-medium text-center">
                  {formatCount(video.reactions.not_interested_count)}
                </span>
              </div>
              
              {/* Comments */}
              <div className={`flex ${isMobile ? 'flex-col items-center space-y-1' : 'flex-col items-center space-y-2'}`}>
                <button
                  onClick={() => onComment(video.id)}
                  className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  <MessageCircle className={`${isMobile ? 'w-4 h-4' : 'w-6 h-6'}`} />
                </button>
                <span className="text-white text-xs font-medium text-center">
                  {formatCount(video.comments_count)}
                </span>
              </div>
              
              {/* Share */}
              <div className={`relative ${isMobile ? 'flex flex-col items-center space-y-1' : ''}`}>
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  <Share2 className={`${isMobile ? 'w-4 h-4' : 'w-6 h-6'}`} />
                </button>
                
                {showShareMenu && (
                  <div className={`absolute ${isMobile ? 'bottom-full left-1/2 transform -translate-x-1/2' : 'bottom-full right-0'} mb-2 bg-white rounded-lg shadow-lg p-2 min-w-[120px] z-20`}>
                    <button
                      onClick={() => handleShare('copy')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded flex items-center space-x-2"
                    >
                      {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      <span>{linkCopied ? 'Copied!' : 'Copy Link'}</span>
                    </button>
                    <button
                      onClick={() => handleShare('facebook')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                    >
                      Facebook
                    </button>
                    <button
                      onClick={() => handleShare('twitter')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                    >
                      Twitter
                    </button>
                    <button
                      onClick={() => handleShare('linkedin')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                    >
                      LinkedIn
                    </button>
                    <button
                      onClick={() => handleShare('whatsapp')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                    >
                      WhatsApp
                    </button>
                  </div>
                )}
              </div>
              
              {/* Volume */}
              {!isMobile && (
                <button
                  onClick={toggleMute}
                  className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Click outside to close share menu */}
      {showShareMenu && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setShowShareMenu(false)}
        />
      )}
    </div>
  );
};

export default VideoCard;